/**
 * Which priority the Emergency form files at (19 Oct, incident 940).
 *
 * The server decides (`api/services/incident_paths.py`); this mirrors the rule
 * so the page can say honestly what happens before the person presses the
 * button: a cybersecurity issue that affects one person is a Priority 2, every
 * other critical report a Priority 1.
 */

export type EmergencyPriority = "P1" | "P2";

export function emergencyPriority(kind: string | null, affected: string): EmergencyPriority {
  return kind === "SECURITY" && affected === "ONE" ? "P2" : "P1";
}

/**
 * The priority the server actually filed (`TicketDetail.priority_code`), as
 * the number the confirmation shows ("1"-"3"), or null otherwise (20 Oct).
 * The page states what was filed, never what it hoped for. P4 is left out:
 * the app's P4 stands for Ivanti's 4 and 5 (a suspicious email with nothing
 * clicked is a 5), so "Priority 4" could be wrong.
 */
export function filedPriorityNumber(code: string | null | undefined): string | null {
  const match = /^P([1-3])$/.exec(code ?? "");
  return match === null ? null : (match[1] ?? null);
}

/**
 * The Impact / Urgency the form sends, matching the server's rule (older
 * servers that ignore `emergency` still get the right codes).
 */
export function emergencyCodes(priority: EmergencyPriority): {
  impact_code: "HIGH" | "MEDIUM";
  urgency_code: "HIGH";
} {
  return priority === "P2"
    ? { impact_code: "MEDIUM", urgency_code: "HIGH" }
    : { impact_code: "HIGH", urgency_code: "HIGH" };
}

/** The page for one suspicious email (linked from the form and the chat). */
export const SUSPICIOUS_EMAIL_PATH = "/report-suspicious-email";

/**
 * The cybersecurity panel on the Emergency form (20 Oct).
 *
 * Picking the SECURITY option opens a panel that asks "What happened?" with
 * three choices, so a single suspicious email no longer has to find a small
 * link. The two email choices hand over to `/report-suspicious-email`, which
 * files the report (the server sets priority and team from its answer: No is
 * low, Yes or not sure a Priority 2, both to the Security Team). The third
 * choice carries on with the Emergency form, where one person is a Priority 2
 * and more people a Priority 1 (above). Nothing here sets a priority.
 */

/** In the order shown: the common case first. */
export const SECURITY_CHOICES = ["EMAIL", "CLICKED", "OTHER"] as const;
export type SecurityChoice = (typeof SECURITY_CHOICES)[number];

/** `?clicked=yes` opens the suspicious email page with "Yes" already chosen. */
export const CLICKED_PARAM = "clicked";

/**
 * Where a choice leads: the suspicious email page (with "Yes" chosen for
 * CLICKED), or `null` to carry on with the Emergency form.
 */
export function securityChoiceTarget(choice: SecurityChoice): string | null {
  switch (choice) {
    case "EMAIL":
      return SUSPICIOUS_EMAIL_PATH;
    case "CLICKED":
      return `${SUSPICIOUS_EMAIL_PATH}?${CLICKED_PARAM}=yes`;
    case "OTHER":
      return null;
  }
}

/**
 * Whether the rest of the Emergency form shows. With SECURITY picked it waits
 * for "something else", so there is one clear way in for an email.
 */
export function showEmergencyDetails(kind: string | null, choice: SecurityChoice | null): boolean {
  return kind !== "SECURITY" || choice === "OTHER";
}

/** The suspicious email page's starting answer, from its query string. */
export function initialClickedAnswer(search: string): "YES" | null {
  const value = new URLSearchParams(search).get(CLICKED_PARAM);
  return value?.toLowerCase() === "yes" ? "YES" : null;
}
